import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { MatLegacyOption as MatOption } from '@angular/material/legacy-core';
import { DeclineOfferUpdate, DeclineReasons, VendorOptions } from 'src/app/shared/models/offers.model';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

export enum TrueFalseEnum {
    False = 0,
    True = 1
}

export enum ReasonEnum {
    SigningDifferentFacility = 11,
    SigningThisFacility = 12,
    SigningAya = 10,
    BetterPay = 17,
    Other = 4,
    Unknown = 22
}

export enum VendorEnum {
    Other = 0
}

@UntilDestroy()
@Component({
    selector: 'app-shared-dialog-decline-offer-component',
    templateUrl: './decline-dialog.component.html',
    styleUrls: ['./decline-dialog.component.scss']
})
export class DialogSharedDeclineOfferComponent implements OnInit {
    @Output() doVendorSearch = new EventEmitter();
    @Output() doFacilitySearch = new EventEmitter();
    @ViewChild('subReasonOption') private subReasonOption: MatOption;

    offerDeclineFormGroup: UntypedFormGroup;
    title: string;
    declineReasons: DeclineReasons[];
    subReasonsList: ListItem[];
    trueFalseEnum = TrueFalseEnum;
    reasonEnum = ReasonEnum;
    vendorEnum = VendorEnum;
    incomingDeclineData: any;
    showPayFields = false;
    disableSubReasonUnknown = false;
    disableSubRasonOtherOptions = false;
    showOtherTextField = false;
    isAgencyLoading = false;
    isFacilityLoading = false;
    isCommentsFieldRequired = false;
    featureFlag = FeatureFlag;

    constructor(
        private readonly _fb: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<DialogSharedDeclineOfferComponent>
    ) {
        this.incomingDeclineData = data;
        this.title = data.title;
        this.declineReasons = data.declineReasons;
    }

    ngOnInit(): void {
        this.offerDeclineFormGroup = this._fb.group({
            reasonId: new UntypedFormControl(null, [Validators.required]),
            vendorId: new UntypedFormControl(null),
            vendorName: new UntypedFormControl(''),
            subReasonIds: new UntypedFormControl(),
            pay: new UntypedFormControl(),
            payRate: new UntypedFormControl({ value: null, disabled: true }, [Validators.pattern('^[0-9]*$')]),
            payFreq: new UntypedFormControl({ value: 'week', disabled: true }),
            isExistingVendorForCandidate: new UntypedFormControl(),
            facilityId: new UntypedFormControl(null),
            facilityName: new UntypedFormControl(''),
            reasonText: new UntypedFormControl('', Validators.maxLength(999)),
            otherReasonText: new UntypedFormControl('')
        });

        this.offerDeclineFormGroup
            .get('pay')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((val) => {
                const isEnterPayFieldSelected = Number(val) === this.trueFalseEnum.True;

                const payRateField = this.offerDeclineFormGroup.controls['payRate'];
                const payFreqField = this.offerDeclineFormGroup.controls['payFreq'];

                if (isEnterPayFieldSelected) {
                    payRateField.setValidators([Validators.required, Validators.pattern('^[0-9]*$')]);
                    payFreqField.setValidators(Validators.required);
                    payRateField.enable();
                    payFreqField.enable();
                } else {
                    payRateField.clearValidators();
                    payFreqField.clearValidators();
                    payRateField.disable();
                    payFreqField.disable();
                    payRateField.setValue(null);
                }

                payRateField.updateValueAndValidity();
                payFreqField.updateValueAndValidity();
            });

        this.offerDeclineFormGroup
            .get('subReasonIds')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((val) => {
                const otherReasonText = this.offerDeclineFormGroup.get('otherReasonText');
                if (val && val.length === 0) {
                    this.disableSubReasonUnknown = false;
                }
                if (val && val.indexOf(this.reasonEnum.Other) > -1) {
                    this.showOtherTextField = true;
                    otherReasonText.setValidators(Validators.required);
                    otherReasonText.updateValueAndValidity();
                } else {
                    this.showOtherTextField = false;
                    otherReasonText.clearValidators();
                    otherReasonText.updateValueAndValidity();
                }

                if (val && val.indexOf(this.reasonEnum.Other) > -1) {
                    this.showOtherTextField = true;
                    otherReasonText.setValidators(Validators.required);
                    otherReasonText.updateValueAndValidity();
                } else {
                    this.showOtherTextField = false;
                    otherReasonText.clearValidators();
                    otherReasonText.updateValueAndValidity();
                }

                this.showPayFields = val && val.indexOf(this.reasonEnum.BetterPay) > -1 ? true : false;
            });

        this.offerDeclineFormGroup
            .get('vendorId')
            .valueChanges.pipe(
                filter((res) => res && res.length > 2),
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.isAgencyLoading = true;
                }),
                untilDestroyed(this)
            )
            .subscribe((res) => {
                if (res) {
                    this.doVendorSearch.emit(res);
                }
            });

        this.offerDeclineFormGroup
            .get('facilityId')
            .valueChanges.pipe(
                filter((res) => res && res.length > 2),
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.isFacilityLoading = true;
                }),
                untilDestroyed(this)
            )
            .subscribe((res) => {
                if (res) {
                    this.doFacilitySearch.emit(res);
                }
            });
    }

    subReasonSelect(event, option) {
        if (event.isUserInput && event.source.value === this.reasonEnum.Unknown) {
            if (option.selected) {
                this.disableSubRasonOtherOptions = true;
            } else {
                this.disableSubRasonOtherOptions = false;
            }
        } else if (option.selected && event.isUserInput && event.source.value !== this.reasonEnum.Unknown) {
            this.disableSubReasonUnknown = true;
        }
    }

    checkSubReasonDisabled(option) {
        if (!this.disableSubRasonOtherOptions && option && option.value !== this.reasonEnum.Unknown) {
            return false;
        } else if (this.disableSubRasonOtherOptions && option && option.value !== this.reasonEnum.Unknown) {
            return true;
        } else if (this.disableSubReasonUnknown && option && option.value === this.reasonEnum.Unknown) {
            return true;
        } else if (!this.disableSubReasonUnknown && option && option.value === this.reasonEnum.Unknown) {
            return false;
        } else {
            return false;
        }
    }

    clearFormValidationOnReasonChange(controls: AbstractControl<any, any>[]) {
        controls.forEach((element) => {
            element.clearValidators();
            element.updateValueAndValidity();
        });
    }

    reasonSelection(event) {
        if (event.isUserInput) {
            const selection = event.source.value;
            this.subReasonsList = selection.subReasons?.length ? selection.subReasons : [];
            const subReasonIdsField = this.offerDeclineFormGroup.get('subReasonIds');
            const vendorIdField = this.offerDeclineFormGroup.get('vendorId');
            const facilityIdField = this.offerDeclineFormGroup.get('facilityId');
            const payField = this.offerDeclineFormGroup.get('pay');
            const isExistingVendorForCandidateField = this.offerDeclineFormGroup.get('isExistingVendorForCandidate');
            const otherReasonText = this.offerDeclineFormGroup.get('reasonText');
            const listFormControls = [
                subReasonIdsField,
                vendorIdField,
                facilityIdField,
                payField,
                isExistingVendorForCandidateField,
                otherReasonText
            ];
            this.clearFormValidationOnReasonChange(listFormControls);
            this.offerDeclineFormGroup.get('reasonId').patchValue(selection.id);
            this.isCommentsFieldRequired = false;
            switch (selection.id) {
                case this.reasonEnum.SigningAya:
                    subReasonIdsField.setValidators(Validators.required);
                    facilityIdField.setValidators(Validators.required);
                    subReasonIdsField.updateValueAndValidity();
                    facilityIdField.updateValueAndValidity();
                    break;

                case this.reasonEnum.SigningDifferentFacility:
                    vendorIdField.setValidators(Validators.required);
                    subReasonIdsField.setValidators(Validators.required);
                    facilityIdField.setValidators(Validators.required);
                    isExistingVendorForCandidateField.setValidators(Validators.required);
                    vendorIdField.updateValueAndValidity();
                    subReasonIdsField.updateValueAndValidity();
                    facilityIdField.updateValueAndValidity();
                    isExistingVendorForCandidateField.updateValueAndValidity();
                    break;

                case this.reasonEnum.SigningThisFacility:
                    vendorIdField.setValidators(Validators.required);
                    subReasonIdsField.setValidators(Validators.required);
                    isExistingVendorForCandidateField.setValidators(Validators.required);
                    vendorIdField.updateValueAndValidity();
                    subReasonIdsField.updateValueAndValidity();
                    isExistingVendorForCandidateField.updateValueAndValidity();
                    break;

                case this.reasonEnum.Other:
                    otherReasonText.addValidators([Validators.required, Validators.minLength(1)]);
                    otherReasonText.updateValueAndValidity();
                    this.isCommentsFieldRequired = true;
                    this.showPayFields = false;
                    break;

                default:
                    this.showPayFields = false;
                    break;
            }
        }
    }

    getName(option: any) {
        return option ? option.name : null;
    }

    checkValue = (agency: any): string => {
        const vendorNameField = this.offerDeclineFormGroup.get('vendorName');
        if (agency && agency.id === 0) {
            vendorNameField.setValidators(Validators.required);
        } else {
            vendorNameField.clearValidators();
        }
        vendorNameField.updateValueAndValidity();
        return agency ? `${agency.legalName}` : '';
    };

    getVendorId(vendor: any) {
        return vendor ? vendor.id : null;
    }

    getChipName(subReason: number) {
        return this.subReasonsList.filter((x) => x.id === subReason).map((x) => x.name);
    }

    checkFacilityValue = (facility: any): string => {
        const facilityNameField = this.offerDeclineFormGroup.get('facilityName');
        if (facility && facility.id === 0) {
            facilityNameField.setValidators(Validators.required);
        } else {
            facilityNameField.clearValidators();
        }
        facilityNameField.updateValueAndValidity();
        return facility ? `${facility.name}` : '';
    };

    selectedVendor(event: MatAutocompleteSelectedEvent): void {
        const vendor = event.option.value as VendorOptions;
        const vendorNameField = this.offerDeclineFormGroup.get('vendorName');
        if (vendor.id === 0) {
            vendorNameField.setValidators(Validators.required);
        } else {
            vendorNameField.clearValidators();
        }
        vendorNameField.updateValueAndValidity();
    }

    onSubmit() {
        this.offerDeclineFormGroup.markAllAsTouched();
        if (this.offerDeclineFormGroup.invalid) {
            return;
        } else {
            const formVals = this.offerDeclineFormGroup.value;
            const cleanedUpForm: DeclineOfferUpdate = {
                reasonId: JSON.parse(formVals.reasonId.id),
                facilityId: formVals.facilityId ? formVals.facilityId.id : null,
                facilityName: '',
                reasonText: formVals.otherReasonText
                    ? formVals.reasonText.concat(' \n', formVals.otherReasonText)
                    : formVals.reasonText,
                subReasonIds: formVals.subReasonIds === null ? [] : formVals.subReasonIds,
                payRate: formVals.payRate ? JSON.parse(formVals.payRate) : null,
                payFreq: formVals.payFreq ? formVals.payFreq : '',
                vendorId: formVals.vendorId ? formVals.vendorId.id : null,
                vendorName: formVals.vendorName,
                isExistingVendorForCandidate: JSON.parse(formVals.isExistingVendorForCandidate)
            };
            this.dialogRef.close(cleanedUpForm);
        }
    }

    close() {
        this.dialogRef.close();
    }
}
